import { useState } from 'react';

interface ResearchDesignsProps {
  designs: any[];
  isAdmin?: boolean;
  currentUser?: any;
  userProfiles?: Record<string, string>;
  onUpdate?: () => void;
  onEdit?: (item: any) => void;
  onDelete?: (id: string) => void;
}

function UserBadge({ username }: { username?: string }) {
  if (!username) return null;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-50 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 rounded-full text-xs font-medium border border-teal-100 dark:border-teal-700 whitespace-nowrap">
      <i className="ri-user-line text-xs"></i>
      {username}
    </span>
  );
}

export default function ResearchDesigns({
  designs,
  isAdmin = false,
  currentUser,
  userProfiles = {},
  onUpdate,
  onEdit,
  onDelete,
}: ResearchDesignsProps) {
  // Initialise expanded set with the first design (if any)
  const initialExpanded =
    designs?.[0]?.id ? new Set<string>([designs[0].id]) : new Set<string>();
  const [expandedVersions, setExpandedVersions] =
    useState<Set<string>>(initialExpanded);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // Determine if the current user can edit/delete a design
  const canModify = (item: any) => {
    if (isAdmin) return true;
    if (!currentUser) return false;
    return item.user_id === currentUser.id;
  };

  const toggleVersion = (id: string) => {
    setExpandedVersions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };

  const handleDelete = (id: string) => {
    if (onDelete) {
      onDelete(id);
      setDeleteConfirm(null);
    }
  };

  // Guard against missing/empty data
  if (!designs || designs.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8">
        <h2 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center bg-teal-50 dark:bg-teal-900/40 rounded-lg">
            <i className="ri-layout-line text-teal-600 dark:text-teal-400"></i>
          </div>
          研究設計
        </h2>
        <p className="text-gray-500 dark:text-gray-400 text-sm mt-4">
          研究設計がまだ登録されていません。
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <h2 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
        <div className="w-8 h-8 flex items-center justify-center bg-teal-50 dark:bg-teal-900/40 rounded-lg">
          <i className="ri-layout-line text-teal-600 dark:text-teal-400"></i>
        </div>
        研究設計
      </h2>

      {/* Design list */}
      {designs.map((design, index) => {
        const isLatest = index === 0;
        const isExpanded = expandedVersions.has(design.id);
        const userCanModify = canModify(design);
        const username = design.user_profiles?.username || null;

        return (
          <div
            key={design.id}
            className={`bg-white dark:bg-gray-800 rounded-xl border overflow-hidden transition-all ${
              isLatest ? 'border-teal-300 dark:border-teal-600 shadow-md' : 'border-gray-200 dark:border-gray-700'
            }`}
          >
            {/* Collapsible header */}
            <button
              onClick={() => toggleVersion(design.id)}
              className="w-full px-5 sm:px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
            >
              <div className="flex items-center gap-3 flex-wrap">
                {isLatest && (
                  <span className="bg-gradient-to-r from-teal-500 to-emerald-600 text-white text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
                    最新版
                  </span>
                )}
                <span className="font-bold text-gray-800 dark:text-white text-sm">
                  バージョン {design.version}
                </span>
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  {new Date(design.created_at).toLocaleDateString('ja-JP')}
                </span>
                {username && <UserBadge username={username} />}
              </div>

              <div className="flex items-center gap-2">
                {userCanModify && onEdit && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onEdit(design); }}
                    className="w-8 h-8 flex items-center justify-center text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/40 rounded-lg transition-colors cursor-pointer"
                    title="編集"
                  >
                    <i className="ri-edit-line text-base"></i>
                  </button>
                )}
                {userCanModify && onDelete && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setDeleteConfirm(design.id); }}
                    className="w-8 h-8 flex items-center justify-center text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded-lg transition-colors cursor-pointer"
                    title="削除"
                  >
                    <i className="ri-delete-bin-line text-base"></i>
                  </button>
                )}
                <i
                  className={`ri-arrow-${
                    isExpanded ? 'up' : 'down'
                  }-s-line text-xl text-gray-400 dark:text-gray-500`}
                ></i>
              </div>
            </button>

            {/* Expanded content */}
            {isExpanded && (
              <div className="border-t border-gray-100 dark:border-gray-700">
                {/* Change log for non‑latest versions */}
                {!isLatest && design.change_log && (
                  <div className="px-5 sm:px-6 py-4 bg-amber-50/60 dark:bg-amber-900/10 border-b border-amber-100 dark:border-amber-800/30">
                    <h4 className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-2 flex items-center gap-1.5">
                      <i className="ri-edit-line"></i>前版からの変更点
                    </h4>
                    <p className="text-sm text-amber-900 dark:text-amber-200 leading-relaxed whitespace-pre-line">
                      {design.change_log}
                    </p>
                  </div>
                )}

                {/* Background & Gap */}
                <div className="px-5 sm:px-6 py-5 border-b border-gray-100 dark:border-gray-700">
                  <h4 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">
                    背景とギャップ
                  </h4>
                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">
                    {design.background_gap}
                  </p>
                </div>

                {/* Mechanism figure */}
                {design.mechanism_figure && (
                  <div className="px-5 sm:px-6 py-5 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
                    <h4 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-4">
                      メカニズム図
                    </h4>
                    <div className="bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 p-3">
                      <img
                        src={design.mechanism_figure}
                        alt="メカニズム図"
                        className="w-full h-auto rounded"
                      />
                    </div>
                  </div>
                )}

                {/* Variable dictionary */}
                <div className="px-5 sm:px-6 py-5 border-b border-gray-100 dark:border-gray-700">
                  <h4 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-4">
                    変数辞書
                  </h4>
                  <div className="overflow-x-auto -mx-5 sm:-mx-6 px-5 sm:px-6">
                    <table className="w-full text-sm min-w-[700px]">
                      <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                        <tr>
                          <th className="px-3 py-2.5 text-left font-semibold text-gray-600 dark:text-gray-300 text-xs whitespace-nowrap">変数名</th>
                          <th className="px-3 py-2.5 text-left font-semibold text-gray-600 dark:text-gray-300 text-xs whitespace-nowrap">種別</th>
                          <th className="px-3 py-2.5 text-left font-semibold text-gray-600 dark:text-gray-300 text-xs">定義</th>
                          <th className="px-3 py-2.5 text-left font-semibold text-gray-600 dark:text-gray-300 text-xs">代理指標</th>
                          <th className="px-3 py-2.5 text-left font-semibold text-gray-600 dark:text-gray-300 text-xs whitespace-nowrap">データ源</th>
                          <th className="px-3 py-2.5 text-left font-semibold text-gray-600 dark:text-gray-300 text-xs">注意</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {Array.isArray(design.variable_dictionary) &&
                          design.variable_dictionary.map((variable: any, idx: number) => (
                            <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                              <td className="px-3 py-2.5 font-mono text-xs text-gray-800 dark:text-gray-200 whitespace-nowrap">{variable.name}</td>
                              <td className="px-3 py-2.5">
                                <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded whitespace-nowrap ${
                                  variable.type === '被説明' ? 'bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400'
                                  : variable.type === '説明' ? 'bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300'
                                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                                }`}>
                                  {variable.type}
                                </span>
                              </td>
                              <td className="px-3 py-2.5 text-xs text-gray-700 dark:text-gray-300">{variable.definition}</td>
                              <td className="px-3 py-2.5 text-xs text-gray-700 dark:text-gray-300">{variable.proxy}</td>
                              <td className="px-3 py-2.5 text-xs text-gray-700 dark:text-gray-300 whitespace-nowrap">{variable.source}</td>
                              <td className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400">{variable.note}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Data candidates */}
                <div className="px-5 sm:px-6 py-5 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
                  <h4 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-4">
                    データ候補
                  </h4>
                  <div className="overflow-x-auto -mx-5 sm:-mx-6 px-5 sm:px-6">
                    <table className="w-full text-sm min-w-[600px]">
                      <thead className="bg-white dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                        <tr>
                          <th className="px-3 py-2.5 text-left font-semibold text-gray-600 dark:text-gray-300 text-xs">データ名</th>
                          <th className="px-3 py-2.5 text-left font-semibold text-gray-600 dark:text-gray-300 text-xs whitespace-nowrap">単位</th>
                          <th className="px-3 py-2.5 text-left font-semibold text-gray-600 dark:text-gray-300 text-xs whitespace-nowrap">期間</th>
                          <th className="px-3 py-2.5 text-left font-semibold text-gray-600 dark:text-gray-300 text-xs">入手方法</th>
                          <th className="px-3 py-2.5 text-left font-semibold text-gray-600 dark:text-gray-300 text-xs">制約</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
                        {Array.isArray(design.data_candidates) &&
                          design.data_candidates.map((data: any, idx: number) => (
                            <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                              <td className="px-3 py-2.5 font-medium text-xs text-gray-800 dark:text-gray-200">{data.name}</td>
                              <td className="px-3 py-2.5 text-xs text-gray-700 dark:text-gray-300 whitespace-nowrap">{data.unit}</td>
                              <td className="px-3 py-2.5 text-xs text-gray-700 dark:text-gray-300 whitespace-nowrap">{data.period}</td>
                              <td className="px-3 py-2.5 text-xs text-gray-700 dark:text-gray-300">{data.method}</td>
                              <td className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400">{data.constraints}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Identification strategy */}
                <div className="px-5 sm:px-6 py-5 border-b border-gray-100 dark:border-gray-700">
                  <h4 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">
                    識別戦略
                  </h4>
                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">
                    {design.identification_strategy}
                  </p>
                </div>

                {/* Limitations */}
                <div className="px-5 sm:px-6 py-5 bg-rose-50/50 dark:bg-rose-900/10">
                  <h4 className="text-xs font-semibold text-rose-600 dark:text-rose-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <i className="ri-alert-line"></i>限界と改善案
                  </h4>
                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">
                    {design.limitations}
                  </p>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setDeleteConfirm(null)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-rose-100 dark:bg-rose-900/40 rounded-full">
                <i className="ri-alert-line text-rose-600 dark:text-rose-400 text-xl"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-800 dark:text-white">削除の確認</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">この操作は取り消せません</p>
              </div>
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-6">
              この研究設計を削除してもよろしいですか？
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                キャンセル
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 px-4 py-2.5 bg-rose-600 hover:bg-rose-700 text-white font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                削除する
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}